/**
 * Shared variant image swatches component.
 * Shows small square thumbnails from variant_combinations[].image.
 * Falls back to colored dots when no images are set.
 */

const COLOR_MAP = {
  red:"#ef4444", blue:"#3b82f6", green:"#22c55e", yellow:"#eab308",
  orange:"#f97316", purple:"#a855f7", pink:"#ec4899", brown:"#92400e",
  black:"#1e293b", white:"#f1f5f9", grey:"#94a3b8", gray:"#94a3b8",
  navy:"#1e3a5f", beige:"#d4b896", gold:"#fbbf24", silver:"#94a3b8",
  cream:"#f5f0e0", maroon:"#7f1d1d", cyan:"#06b6d4", teal:"#14b8a6",
};

export default function VariantSwatches({ product, max = 4 }) {
  const combos = product.variant_combinations || [];
  const options = product.variant_options || [];

  // Prefer "color" option, else take the first
  const option =
    options.find(o => o.name?.toLowerCase() === "color") || options[0];
  if (!option?.values?.length) return null;

  const key = option.name.toLowerCase();

  // Build unique per-value entries from combos (preserves variant image)
  const seen = new Set();
  const variants = [];
  for (const combo of combos) {
    const val = combo[key];
    if (!val || seen.has(val)) continue;
    seen.add(val);
    variants.push({ label: val, image: combo.image || null });
  }

  // Fallback: use option values when no combos
  if (!variants.length) {
    for (const v of option.values) {
      if (!seen.has(v)) {
        seen.add(v);
        variants.push({ label: v, image: null });
      }
    }
  }

  if (!variants.length) return null;

  const hasImages = variants.some(v => v.image);
  const shown = variants.slice(0, max);
  const extra = variants.length - max;

  return (
    <div className="flex gap-1 flex-wrap items-center">
      {shown.map((v, i) =>
        hasImages && v.image ? (
          <img
            key={i}
            src={v.image}
            alt={v.label}
            title={v.label}
            className="w-6 h-6 rounded-md object-cover border border-slate-200 flex-shrink-0"
            onError={e => { e.target.style.display = "none"; }}
          />
        ) : (
          <span
            key={i}
            title={v.label}
            className="inline-block w-4 h-4 rounded-full border border-slate-200 flex-shrink-0"
            style={{ background: COLOR_MAP[v.label.toLowerCase()] || "#94a3b8" }}
          />
        )
      )}
      {extra > 0 && (
        <span className="text-[10px] text-slate-400 font-medium">+{extra}</span>
      )}
    </div>
  );
}
